import { useEffect } from 'react';

import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';

import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import {
  CloseCircleOutline,
  CheckOutline,
  DropdownInput,
  useToast,
} from '@aslanpay/portal-common-lib';

import { PointsTransactionReasonLabels } from 'app/constants/pointsTransactions';

import useRewardPoints from 'app/hooks/useRewardPoints';

import { rewardPointsSchema } from 'app/utils/schemas';

import { getFilteredPointsTransactionReasonOptions } from 'app/utils/gifting';

import { useParams } from 'react-router-dom';

import useFetchIndividual from 'app/hooks/useFetchIndividual';

import { RewardPointsFormFields, RewardPointsModalProps } from './types';

function RewardPointsModal({ id, isOpen, onClose, onRewardPoints }: RewardPointsModalProps) {
  const toast = useToast();

  const { individualId: urlId } = useParams();

  const individualId = id || urlId;

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<RewardPointsFormFields>({
    mode: 'onBlur',
    defaultValues: {
      transactionReason: undefined,
      pointsAmount: undefined,
      notes: '',
      referralId: '',
      companyId: '',
      individualId,
    },
    resolver: yupResolver(rewardPointsSchema),
  });

  const { data: individual } = useFetchIndividual(individualId);

  const handleClose = () => {
    reset();
    onClose();
  };

  const { mutate: rewardPoints, isLoading: isRewardingPoints } = useRewardPoints({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Points rewarded successfully!',
      });

      onRewardPoints();
      handleClose();
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, it was not possible to reward points. Please try again.',
      });
    },
  });

  const handleRewardPoints = (data: RewardPointsFormFields) => {
    const { notes, pointsAmount, referralId, transactionReason } = data;

    if (!individualId) {
      toast({
        status: 'warning',
        title: 'Missing Individual ID',
        description: 'Could not find an individual ID to reward points.',
      });
      return;
    }

    rewardPoints({
      individualIds: [individualId],
      transactionReason,
      pointsAmount,
      notes,
      companyId: individual?.companyId,
      referralId,
    });
  };

  const pointsTransactionReasonOptions = getFilteredPointsTransactionReasonOptions();

  const watchReason = watch('transactionReason');

  useEffect(() => {
    if (watchReason) {
      setValue('notes', PointsTransactionReasonLabels[watchReason].notes);
    }
  }, [setValue, watchReason]);

  return (
    <Modal
      isOpen={isOpen}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      onClose={handleClose}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Text as="h6">Reward points</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close reward points"
              onClick={handleClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody mt="20px">
          <form id="reward-points-form" onSubmit={handleSubmit(handleRewardPoints)} noValidate>
            <Stack spacing="20px">
              <FormControl isInvalid={!!errors?.pointsAmount?.message} isRequired>
                <FormLabel>Amount</FormLabel>
                <Input
                  type="number"
                  placeholder="Amount of points to reward"
                  {...register('pointsAmount')}
                />
                <FormErrorMessage>{errors?.pointsAmount?.message}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors?.transactionReason?.message} isRequired>
                <FormLabel>Reason</FormLabel>
                <Controller
                  control={control}
                  name="transactionReason"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <DropdownInput
                      placeholder="Select a reason"
                      defaultValue={pointsTransactionReasonOptions.find(
                        (reason) => reason.value === value
                      )}
                      options={pointsTransactionReasonOptions}
                      onChange={onChange}
                      onBlur={onBlur}
                    />
                  )}
                />
                <FormErrorMessage>{errors?.transactionReason?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors?.notes?.message}>
                <FormLabel>Notification reason</FormLabel>
                <Textarea
                  placeholder="Enter the notification reason text here"
                  {...register('notes')}
                />
                <FormErrorMessage>{errors?.notes?.message}</FormErrorMessage>
              </FormControl>
            </Stack>
          </form>
        </ModalBody>
        <ModalFooter>
          <Text color="system.error" textStyle="extraSmallMedium">
            * mandatory
          </Text>
          <Spacer />
          <Button variant="outline" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            form="reward-points-form"
            type="submit"
            size="sm"
            rightIcon={<CheckOutline boxSize="20px" />}
            isLoading={isRewardingPoints}
            isDisabled={!isValid}
          >
            Reward
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default RewardPointsModal;
